<template>
  <div v-if="open" class="pinned-messages-panel open">
    <div class="pinned-messages-panel-head">
      <button class="pinned-messages-panel-back" type="button" aria-label="返回" @click="close">←</button>
      <div class="pinned-messages-panel-title">置顶消息<span v-if="list.length"> · {{ list.length }} 条</span></div>
    </div>
    <div class="pinned-messages-panel-list">
      <div v-if="!list.length" class="pinned-messages-empty">还没有置顶消息</div>
      <template v-else>
        <div
          v-for="item in list"
          :key="item.id || item.messageId"
          class="telegram-row"
          :class="rowSide(item)"
          @contextmenu.prevent.stop="onRowContextMenu($event, item)"
        >
          <div class="telegram-message-avatar" :class="{ self: isSelf(item) }" :style="avatarStyle(item)">
            <template v-if="!senderAvatarUrl(item)">{{ avatarLetter(item) }}</template>
          </div>
          <div class="telegram-message-stack">
            <div class="telegram-bubble" :class="isSelf(item) ? 'outgoing' : 'incoming'">
              <div v-if="!isSelf(item) && senderName(item)" class="telegram-name-line">
                <div class="telegram-group-name">{{ senderName(item) }}</div>
                <span v-if="senderAgentTag(item)" class="telegram-agent-badge">{{ senderAgentTag(item) }}</span>
              </div>

              <!-- 引用回复卡片：点击跳到对话区原消息（结构与 TelegramChat 同款） -->
              <button v-if="replyOf(item)" class="telegram-quote-card" type="button" @click.stop="jumpToReply(item)">
                <div class="telegram-quote-main">
                  <div class="telegram-quote-name">
                    {{ replyOf(item).senderName }}
                    <span v-if="replyOf(item).agentTag" class="telegram-agent-badge">{{ replyOf(item).agentTag }}</span>
                  </div>
                  <div v-if="replyOf(item).fileCount > 1" class="telegram-quote-file">
                    <span class="telegram-quote-file-icon">{{ replyFileIcon(replyOf(item).firstFile) }}</span>
                    <span v-if="replyOf(item).text" class="telegram-quote-file-name">{{ replyOf(item).text }}</span>
                    <span class="telegram-quote-file-count">{{ replyOf(item).fileCount }} 个文件</span>
                  </div>
                  <div v-else-if="replyOf(item).fileCount === 1" class="telegram-quote-file">
                    <span class="telegram-quote-file-icon">{{ replyFileIcon(replyOf(item).firstFile) }}</span>
                    <span class="telegram-quote-file-name">{{ replyOf(item).text || replyOf(item).firstFile.name || '文件' }}</span>
                  </div>
                  <div v-else class="telegram-quote-text">{{ replyOf(item).text || '[消息]' }}</div>
                </div>
                <img v-if="replyOf(item).fileCount && replyFileIsImage(replyOf(item).firstFile)" class="telegram-quote-thumb" :src="replyOf(item).firstFile.thumbnailUrl || replyOf(item).firstFile.url" />
              </button>

              <!-- 多文件（2+） -->
              <div v-if="renderKind(item) === 'multi-file'" class="tg-multi-file-wrap">
                <div class="tg-multi-file-list">
                  <div v-for="(f, fi) in allFiles(item)" :key="f.id || f.url || fi" class="tg-multi-file-item" @click.stop="previewFile(f)">
                    <div v-if="isFileImage(f)" class="tg-mf-img-wrap"><img :src="f.url" class="tg-mf-img" :alt="f.name || '图片'" loading="lazy" /></div>
                    <div v-else-if="isFileVideo(f)" class="tg-mf-img-wrap tg-mf-video-thumb"><video :src="f.url" class="tg-mf-img" preload="metadata" muted playsinline></video><span class="tg-mf-play">▶</span></div>
                    <div v-else-if="isFileAudio(f)" class="tg-mf-doc-icon" style="background:#35B58A">♪</div>
                    <div v-else class="tg-mf-doc-icon">{{ fileExtLabel(f.name || '') }}</div>
                    <div class="tg-mf-info">
                      <span class="tg-mf-name">{{ f.name || '文件' }}</span>
                      <span class="tg-mf-size">{{ formatSize(f.size) }}</span>
                    </div>
                  </div>
                </div>
                <div v-if="captionText(item)" class="telegram-bubble-text" :class="{ 'md-content': isAgent(item) }" style="margin-top:6px" v-html="captionHtml(item)"></div>
              </div>

              <!-- 图片 -->
              <div v-else-if="renderKind(item) === 'image'" class="telegram-bubble-image-wrap">
                <img :src="firstFile(item).url || firstFile(item).thumbnailUrl" class="telegram-bubble-image" :alt="firstFile(item).name || '图片'" loading="lazy" @click.stop="previewFile(firstFile(item))" />
                <div v-if="captionText(item)" class="telegram-bubble-text" :class="{ 'md-content': isAgent(item) }" style="margin-top:6px" v-html="captionHtml(item)"></div>
              </div>

              <!-- 视频 -->
              <div v-else-if="renderKind(item) === 'video'" class="telegram-video-wrap" @click.stop="previewFile(firstFile(item))">
                <div class="telegram-video-thumb">
                  <video :src="firstFile(item).url" class="telegram-bubble-video" preload="metadata" muted playsinline></video>
                  <div class="telegram-video-play">▶</div>
                </div>
                <div v-if="captionText(item)" class="telegram-bubble-text" :class="{ 'md-content': isAgent(item) }" style="margin-top:6px" v-html="captionHtml(item)"></div>
              </div>

              <!-- 语音/audio：与主聊天同款自定义播放器 -->
              <div v-else-if="renderKind(item) === 'audio'" class="tg-audio-wrap" @click.stop>
                <div class="tg-audio-player" :class="{ 'tg-audio-right': isSelf(item) }">
                  <audio
                    :ref="'audio_' + audioId(item)"
                    :src="firstFile(item).url"
                    preload="none"
                    @timeupdate="onAudioTimeUpdate(audioId(item), $event)"
                    @loadedmetadata="onAudioMeta(audioId(item), $event)"
                    @ended="onAudioEnded(audioId(item))"
                  ></audio>
                  <button class="tg-audio-btn" type="button" @click.stop="toggleAudio(audioId(item))">
                    <svg v-if="audioState[audioId(item)] && audioState[audioId(item)].playing" viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
                    <svg v-else viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M8 5v14l11-7z"/></svg>
                  </button>
                  <div class="tg-audio-body">
                    <div class="tg-audio-progress-wrap" @click.stop="seekAudio(audioId(item), $event)">
                      <div class="tg-audio-track">
                        <div class="tg-audio-fill" :style="{ width: audioProgress(audioId(item)) + '%' }"></div>
                        <div class="tg-audio-bars">
                          <span v-for="n in 28" :key="n" class="tg-audio-bar" :class="{ active: (n / 28 * 100) <= audioProgress(audioId(item)) }"></span>
                        </div>
                      </div>
                    </div>
                    <div class="tg-audio-meta-row">
                      <span class="tg-audio-time">{{ audioDisplayTime(audioId(item)) }}</span>
                      <button class="tg-audio-open-ws-btn" type="button" @click.stop="previewFile(firstFile(item))">
                        <span class="tg-audio-open-ws-name">{{ firstFile(item).name || 'Audio' }}</span>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" width="12" height="12"><polyline points="9 18 15 12 9 6"/></svg>
                      </button>
                    </div>
                  </div>
                </div>
                <div v-if="captionText(item)" class="telegram-bubble-text" :class="{ 'md-content': isAgent(item) }" style="margin-top:6px" v-html="captionHtml(item)"></div>
              </div>

              <!-- 文件 -->
              <div v-else-if="renderKind(item) === 'file'" class="telegram-doc-wrap">
                <button class="telegram-file-card" type="button" @click.stop="previewFile(firstFile(item))">
                  <div class="telegram-file-top">
                    <div class="telegram-file-icon">{{ fileExtLabel(firstFile(item).name) }}</div>
                    <div class="telegram-file-copy">
                      <div class="telegram-file-title">{{ firstFile(item).name || '文件' }}</div>
                      <div class="telegram-file-meta">{{ formatSize(firstFile(item).size) }}</div>
                    </div>
                  </div>
                </button>
                <div v-if="captionText(item)" class="telegram-file-caption telegram-bubble-text" :class="{ 'md-content': isAgent(item) }" v-html="captionHtml(item)"></div>
              </div>

              <!-- 系统消息 -->
              <div v-else-if="renderKind(item) === 'system'" class="telegram-bubble-text" style="font-style:italic;color:#94a4b5">
                {{ bubbleText(item) || '[系统消息]' }}
              </div>

              <!-- 纯文本（agent 走 md 渲染） -->
              <div v-else class="telegram-bubble-text" :class="{ 'md-content': isAgent(item) }" v-html="bubbleHtml(item)"></div>

              <div class="telegram-bubble-time">{{ timeText(item) }}</div>
            </div>
            <div class="pinned-messages-meta">
              <span class="pinned-messages-meta-time">📌 置顶于 {{ pinnedAtText(item) }}</span>
              <button class="pinned-messages-item-unpin" type="button" @click.stop="unpin(item)">取消置顶</button>
            </div>
          </div>
        </div>
      </template>
    </div>
    <div
      v-show="ctxMenu.open"
      ref="ctxMenu"
      class="pinned-ctx-menu"
      :style="{ left: ctxMenu.x + 'px', top: ctxMenu.y + 'px' }"
      @click.stop
      @contextmenu.prevent.stop
    >
      <button type="button" class="pinned-ctx-item" @click="onCopyMessage">复制消息</button>
      <button type="button" class="pinned-ctx-item" @click="onForwardMessage">转发</button>
      <button type="button" class="pinned-ctx-item pinned-ctx-item--danger" @click="onUnpinMessage">取消置顶</button>
    </div>
  </div>
</template>

<script>
import { renderMarkdown } from '../utils/markdown'
import { solidFromColor } from '../utils/avatarColor'

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]))
}

// 普通用户文本的格式化，与 TelegramChat 对话区完全一致：
// 转义 → URL 链接化(同正则、同 .telegram-link，含尾随标点剥离) → @提及(同 .telegram-mention) → 换行
// 复用全局 CSS 类，保证置顶面板里的链接/@提及与对话区样式、可点行为一致
function formatPlainText(text) {
  const escaped = escapeHtml(text)
  const withLinks = escaped.replace(
    /(https?:\/\/[^\s<>"'，。！？、）】、。！？]+)/g,
    (raw) => {
      let u = raw, trail = ''
      while (u.length > 0 && /[.,!?;:，。！？；：、【】…—·]$/.test(u[u.length - 1])) {
        trail = u[u.length - 1] + trail
        u = u.slice(0, -1)
      }
      return `<a href="${u}" target="_blank" rel="noopener noreferrer" class="telegram-link">${u}</a>${trail}`
    }
  )
  const withMentions = withLinks.replace(
    /@([一-龥A-Za-z0-9\-_]+)/g,
    (_, name) => `<button class="telegram-mention" type="button" data-mention="@${name}">@${name}</button>`
  )
  return withMentions.replace(/\n/g, '<br>')
}

// 与 TelegramChat 一致的转发消息格式化（覆盖三种类型 + 留言）
function renderForwardHtml(text, kind) {
  text = String(text || '')
  if (!text) return null

  // 1) 单条文本转发：「【转发消息】来自 X：body」
  if (kind === 'text' && text.startsWith('【转发消息】')) {
    const m = text.match(/^【转发消息】来自\s*(.+?)：([\s\S]*)/)
    if (m) {
      const fwdFrom = escapeHtml(m[1])
      let body = m[2] || ''
      let comment = ''
      const idx = body.indexOf('\n留言：')
      if (idx >= 0) { comment = body.slice(idx + 4); body = body.slice(0, idx) }
      // body 走 markdown 渲染(跟 TelegramChat 转发同款):支持 <info-card>/<quick-tasks> 自定义标签 + 自动 URL 转链 + DOMPurify 兜底
      const bodyHtml = renderMarkdown(body)
      let html = `<div class="fwd-card"><div class="fwd-card-head"><svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M15 14l5-5-5-5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><path d="M20 9H9a7 7 0 0 0-7 7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg> 转发消息</div><div class="fwd-card-from">来自 ${fwdFrom}</div><div class="fwd-card-body md-content">${bodyHtml}</div></div>`
      if (comment) html += `<div class="fwd-comment">${escapeHtml(comment)}</div>`
      return html
    }
  }

  // 2) 合并转发：「【合并转发】\n发送者：...\n...」
  if (text.startsWith('【合并转发】')) {
    const lines = text.replace(/^【合并转发】\n?/, '').split('\n').filter(l => l.trim())
    const linesHtml = lines.map(line => {
      let rest = line, nameHtml = ''
      const nm = rest.match(/^(.+?)[：:]/)
      if (nm) { nameHtml = '<div class="fwd-merge-name">' + escapeHtml(nm[1]) + '</div>'; rest = rest.slice(nm[0].length) }
      let imgHtml = ''
      const imgMatch = rest.match(/\[img:([^\]]+)\]/)
      if (imgMatch) { imgHtml = '<img class="fwd-merge-img" src="' + imgMatch[1] + '" />'; rest = rest.replace(imgMatch[0], '') }
      let fileHtml = ''
      const fileMatch = rest.match(/\[file:([^:]+):([^:]+):([^\]]+)\]/)
      if (fileMatch) {
        const fName = escapeHtml(fileMatch[1]), fSize = escapeHtml(fileMatch[2]), fUrl = fileMatch[3]
        const ext = (fName.split('.').pop() || '').toUpperCase()
        fileHtml = `<a class="fwd-merge-file" href="${fUrl}" data-file-url="${fUrl}" data-file-name="${fName}"><div class="telegram-file-top"><div class="telegram-file-icon">${ext}</div><div class="telegram-file-copy"><div class="telegram-file-title">${fName}</div><div class="telegram-file-meta">${fSize}</div></div></div></a>`
        rest = rest.replace(fileMatch[0], '')
      }
      let textHtml = ''
      if (rest.trim()) {
        textHtml = '<div class="fwd-merge-text">' + escapeHtml(rest).replace(/(https?:\/\/[^\s<>"']+)/g,
          u => `<a href="${u}" target="_blank" rel="noopener noreferrer" class="telegram-link">${u}</a>`) + '</div>'
      }
      return '<div class="fwd-merge-line">' + nameHtml + imgHtml + fileHtml + textHtml + '</div>'
    }).join('')
    return `<div class="fwd-card"><div class="fwd-card-head"><svg width="14" height="14" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.6"/><rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.6"/><rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.6"/><rect x="14" y="14" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.6"/></svg> 合并转发</div>${linesHtml}</div>`
  }

  // 3) 带媒体的转发 caption：「转发自 X」 / 「转发自 X: body」
  if (kind !== 'text' && text.startsWith('转发自 ')) {
    let fwd = text, comment = ''
    const idx = text.indexOf('\n留言：')
    if (idx >= 0) { comment = text.slice(idx + 4); fwd = text.slice(0, idx) }
    const m = fwd.match(/^(转发自\s+\S+?)(?:[：:](.*))?$/s)
    if (m) {
      const label = escapeHtml(m[1])
      const rest = (m[2] || '').trim()
      let bodyHtml = ''
      if (rest) {
        bodyHtml = '<div style="margin-top:4px">' + escapeHtml(rest).replace(/(https?:\/\/[^\s<>"']+)/g,
          u => `<a href="${u}" target="_blank" rel="noopener noreferrer" class="telegram-link">${u}</a>`) + '</div>'
      }
      let html = `<div class="fwd-file-label"><svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M15 14l5-5-5-5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><path d="M20 9H9a7 7 0 0 0-7 7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg> ${label}</div>${bodyHtml}`
      if (comment) html += `<div class="fwd-comment">${escapeHtml(comment)}</div>`
      return html
    }
  }
  return null
}

export default {
  name: 'PinnedMessagesPanel',
  props: {
    pinnedKey: { type: String, default: '' },
    conversationId: { type: String, default: '' },
    topicId: { type: String, default: '' }
  },
  data() {
    return { audioState: {}, ctxMenu: { open: false, x: 0, y: 0, item: null } }
  },
  computed: {
    open() { return this.$store.state.pinnedPanelOpen },
    list() {
      const raw = this.$store.state.conversationPinnedMessages[this.pinnedKey] || []
      return raw.slice().reverse()
    },
    currentUserId() { return this.$store.state.currentUserId },
    // 系统/官方会话标志,跟 TelegramChat 的 _renderHtml 分流逻辑保持一致
    // isSystem 和 isService 互斥:只在 isSystem=true 时走系统分流
    isSystemSession() {
      const items = this.$store.state.sessionItems || []
      const cur = items.find(s => s.key === this.$store.state.currentSessionKey)
      return !!(cur && cur.isSystem)
    }
  },
  watch: {
    // 面板关闭时：根 v-if 拆掉面板子树，但 teleport 到 body 的右键菜单不会被带走 →
    // 会变成 body 里游离、v-show 失效、监听已摘的“关不掉的弹窗”。这里主动收尾。
    open(v) {
      if (!v) {
        this.closeCtxMenu()
        const el = this.$refs.ctxMenu
        if (el && el.parentNode === document.body) {
          try { document.body.removeChild(el) } catch (_) {}
        }
      }
    }
  },
  methods: {
    close() { this.$store.commit('SET_PINNED_PANEL_OPEN', false) },
    isSelf(item) {
      const m = item.message || {}
      const senderId = m.sender && (m.sender.id || m.sender.userId)
      return senderId && senderId === this.currentUserId
    },
    isAgent(item) {
      const s = (item.message && item.message.sender) || {}
      return s.type === 'agent'
    },
    rowSide(item) { return this.isSelf(item) ? 'right' : 'left' },
    avatarLetter(item) {
      const s = (item.message && item.message.sender) || {}
      const n = s.name || '?'
      return n.charAt(0)
    },
    senderName(item) {
      const s = (item.message && item.message.sender) || {}
      return s.name || ''
    },
    senderAgentTag(item) { return this.isAgent(item) ? 'Agent' : '' },
    senderAvatarUrl(item) {
      const s = (item.message && item.message.sender) || {}
      return s.avatarUrl || ''
    },
    avatarStyle(item) {
      const url = this.senderAvatarUrl(item)
      if (url) return { backgroundImage: `url(${url})`, backgroundSize: 'cover', backgroundPosition: 'center' }
      const s = (item.message && item.message.sender) || {}
      const bg = solidFromColor(s.avatarColorRgb)
      return bg ? { background: bg } : {}
    },
    allFiles(item) {
      const m = item.message || {}
      return (m.body && m.body.files) || m.files || []
    },
    firstFile(item) {
      const files = this.allFiles(item)
      return files[0] || {}
    },
    isFileImage(f) {
      const mime = (f.mimeType || f.type || '').toLowerCase()
      const name = (f.name || '').toLowerCase()
      return mime.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(name)
    },
    isFileVideo(f) {
      const mime = (f.mimeType || f.type || '').toLowerCase()
      const name = (f.name || '').toLowerCase()
      return mime.startsWith('video/') || /\.(mp4|webm|mov|avi|mkv)$/.test(name)
    },
    isFileAudio(f) {
      const mime = (f.mimeType || f.type || '').toLowerCase()
      const name = (f.name || '').toLowerCase()
      return mime.startsWith('audio/') || /\.(mp3|wav|ogg|m4a|flac)$/.test(name)
    },
    renderKind(item) {
      const m = item.message || {}
      if (m.messageType === 'system' || m.kind === 'system') return 'system'
      const files = this.allFiles(item)
      if (files.length > 1) return 'multi-file'
      const f = files[0]
      if (!f || !f.url) return 'text'
      if (this.isFileImage(f)) return 'image'
      if (this.isFileVideo(f)) return 'video'
      if (this.isFileAudio(f)) return 'audio'
      return 'file'
    },
    bubbleText(item) {
      const m = item.message || {}
      return String((m.body && (m.body.text || m.body.caption)) || m.text || '')
    },
    msgKind(item) {
      const m = item.message || {}
      // 按 messageType 推断 kind（与主聊天 mapApiMessageToUi 类似）
      if (m.messageType === 'text' || (!m.messageType && (!m.body || !m.body.files || !m.body.files.length))) return 'text'
      const k = this.renderKind(item)
      return k === 'multi-file' ? 'file' : (k || 'text')
    },
    bubbleHtml(item) {
      const text = this.bubbleText(item)
      if (!text) return ''
      // 转发文案优先（合并转发 / 单条转发）
      const fwd = renderForwardHtml(text, this.msgKind(item))
      if (fwd) return fwd
      // 系统/官方会话:跟 TelegramChat _renderHtml 同分流;转发已在前面 return,这里不会命中
      if (this.isSystemSession) {
        return text.trim().startsWith('<') ? text : renderMarkdown(text)
      }
      return this.isAgent(item) ? renderMarkdown(text) : formatPlainText(text)
    },
    captionText(item) {
      const m = item.message || {}
      return (m.body && (m.body.caption || m.body.text)) || ''
    },
    captionHtml(item) {
      const t = this.captionText(item)
      if (!t) return ''
      // 带文件的转发（caption 以「转发自」开头）
      const fwd = renderForwardHtml(t, this.msgKind(item))
      if (fwd) return fwd
      if (this.isSystemSession) {
        return t.trim().startsWith('<') ? t : renderMarkdown(t)
      }
      return this.isAgent(item) ? renderMarkdown(t) : formatPlainText(t)
    },
    replyOf(item) {
      const m = item.message || {}
      const r = m.replyTo
      if (!r) return null
      const sender = r.sender || {}
      const files = (r.body && r.body.files) || r.files || []
      const text = (r.body && (r.body.text || r.body.caption)) || r.text || ''
      return {
        messageId: r.id || r.messageId || r.message_id || '',
        topicId: r.topicId || r.topic_id || '',
        senderName: sender.name || '',
        agentTag: sender.type === 'agent' ? 'Agent' : '',
        text,
        fileCount: files.length,
        firstFile: files[0] || {}
      }
    },
    replyFileIcon(f) {
      if (!f) return '📎'
      if (this.isFileImage(f)) return '🖼'
      if (this.isFileVideo(f)) return '🎬'
      if (this.isFileAudio(f)) return '🎵'
      return '📎'
    },
    replyFileIsImage(f) { return f && this.isFileImage(f) },
    fileExtLabel(name) {
      if (!name) return '文件'
      const m = name.match(/\.([a-zA-Z0-9]{1,5})$/)
      return m ? m[1].toUpperCase() : '文件'
    },
    formatSize(size) {
      if (!size || size <= 0) return ''
      if (size < 1024) return size + ' B'
      if (size < 1024 * 1024) return (size / 1024).toFixed(1) + ' KB'
      if (size < 1024 * 1024 * 1024) return (size / 1024 / 1024).toFixed(1) + ' MB'
      return (size / 1024 / 1024 / 1024).toFixed(2) + ' GB'
    },
    timeText(item) {
      const m = item.message
      const ts = m && (m.createdAt || m.created_at)
      if (!ts) return ''
      try {
        const d = new Date(ts)
        return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0')
      } catch { return '' }
    },
    pinnedAtText(item) {
      const ts = item.pinnedAt || item.pinned_at
      if (!ts) return ''
      try {
        const d = new Date(ts)
        const now = new Date()
        const sameDay = d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate()
        const hh = String(d.getHours()).padStart(2, '0')
        const mm = String(d.getMinutes()).padStart(2, '0')
        if (sameDay) return `${hh}:${mm}`
        const M = String(d.getMonth() + 1).padStart(2, '0')
        const day = String(d.getDate()).padStart(2, '0')
        return `${M}/${day} ${hh}:${mm}`
      } catch { return '' }
    },
    async jumpTo(item) {
      const messageId = item.messageId
      if (!messageId || !this.conversationId) return
      this.close()
      const found = await this.$store.dispatch('jumpToMessage', {
        conversationId: this.conversationId,
        topicId: this.topicId || undefined,
        messageId
      })
      if (!found) return
      // 等渲染稳定后滚动到消息并高亮（与主聊天 jumpToQuote 同款）
      await this.$nextTick()
      await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)))
      const el = document.getElementById('msg-' + messageId)
      if (!el) return
      el.scrollIntoView({ block: 'center' })
      el.classList.add('highlight-flash')
      setTimeout(() => el.classList.remove('highlight-flash'), 2000)
    },
    unpin(item) {
      this.$store.dispatch('unpinMessage', {
        conversationId: this.conversationId,
        topicId: this.topicId || undefined,
        messageId: item.messageId
      })
    },
    // ───── 右击菜单：复制 / 转发 / 取消置顶 ─────
    onRowContextMenu(e, item) {
      // teleport 菜单到 body：.pinned-messages-panel 有 backdrop-filter，会把自己变成 fixed 子代的
      // containing-block，导致菜单贴到 panel 角落而不是鼠标位置。每次右击都查一下，parentNode
      // 检查保证幂等（已经在 body 上就不重复 append）。
      const el = this.$refs.ctxMenu
      if (el && el.parentNode !== document.body) document.body.appendChild(el)
      // 计算钳制到视口内（菜单宽 ~120、高 ~120，留 8px 边距）
      const vw = window.innerWidth, vh = window.innerHeight
      const x = Math.min(e.clientX, vw - 128)
      const y = Math.min(e.clientY, vh - 128)
      this.ctxMenu = { open: true, x, y, item }
      // 推迟到下一 tick 再挂监听，避免本次右键事件立刻触发 closeCtxMenu
      setTimeout(() => {
        document.addEventListener('click', this.closeCtxMenu)
        document.addEventListener('contextmenu', this.closeCtxMenu)
        document.addEventListener('scroll', this.closeCtxMenu, true)
        window.addEventListener('resize', this.closeCtxMenu)
        window.addEventListener('keydown', this.onCtxKeydown)
      }, 0)
    },
    closeCtxMenu() {
      if (this.ctxMenu.open) this.ctxMenu = { open: false, x: 0, y: 0, item: null }
      document.removeEventListener('click', this.closeCtxMenu)
      document.removeEventListener('contextmenu', this.closeCtxMenu)
      document.removeEventListener('scroll', this.closeCtxMenu, true)
      window.removeEventListener('resize', this.closeCtxMenu)
      window.removeEventListener('keydown', this.onCtxKeydown)
    },
    onCtxKeydown(e) { if (e.key === 'Escape') this.closeCtxMenu() },
    onCopyMessage() {
      const it = this.ctxMenu.item
      this.closeCtxMenu()
      if (!it) return
      // 取消息纯文本：纯文本 → bubbleText；带文件且有 caption → 用 caption；纯文件 → 用文件名
      const text = (this.bubbleText(it) || '').trim()
        || (this.captionText(it) || '').trim()
        || (this.firstFile(it).name || '')
      if (!text) return
      // 优先用 async clipboard，回退到 textarea + execCommand
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).catch(() => this._copyFallback(text))
      } else {
        this._copyFallback(text)
      }
    },
    _copyFallback(text) {
      try {
        const ta = document.createElement('textarea')
        ta.value = text
        ta.style.cssText = 'position:fixed;top:0;left:0;opacity:0;'
        document.body.appendChild(ta)
        ta.focus(); ta.select()
        try { document.execCommand('copy') } catch (_) {}
        document.body.removeChild(ta)
      } catch (_) {}
    },
    onForwardMessage() {
      const it = this.ctxMenu.item
      this.closeCtxMenu()
      if (!it) return
      // 把 pinned item 映射成 FORWARD_MODAL 的 payload（与 TelegramMenus.openForward 同口径）
      const m = it.message || {}
      const senderName = this.senderName(it) || (m.sender && m.sender.name) || '未知'
      const kind = (m.kind || it.kind || 'text')
      const text = this.bubbleText(it) || this.captionText(it) || ''
      const files = this.allFiles(it)
      const first = this.firstFile(it) || {}
      const isImg = this.isFileImage(first)
      this.$store.commit('SET_FORWARD_MODAL', {
        open: true,
        senderName,
        text,
        kind,
        imageUrl: isImg ? (first.url || first.thumbnailUrl || '') : '',
        fileUrl: !isImg ? (first.url || '') : '',
        fileName: first.name || '',
        mimeType: first.mimeType || '',
        files: files && files.length ? files : null,
        imageWidth: first.width || 0,
        imageHeight: first.height || 0,
        fileSize: first.size || 0,
        fileSizeStr: this.formatSize(first.size) || ''
      })
    },
    onUnpinMessage() {
      const it = this.ctxMenu.item
      this.closeCtxMenu()
      if (it) this.unpin(it)
    },
    async jumpToReply(item) {
      const r = this.replyOf(item)
      const messageId = r && r.messageId
      if (!messageId || !this.conversationId) return
      this.close()
      const found = await this.$store.dispatch('jumpToMessage', {
        conversationId: this.conversationId,
        topicId: r.topicId || this.topicId || undefined,
        messageId
      })
      if (!found) return
      await this.$nextTick()
      await new Promise(res => requestAnimationFrame(() => requestAnimationFrame(res)))
      const el = document.getElementById('msg-' + messageId)
      if (!el) return
      el.scrollIntoView({ block: 'center' })
      el.classList.add('highlight-flash')
      setTimeout(() => el.classList.remove('highlight-flash'), 2000)
    },
    // 点击文件直接在工作区预览（与主聊天 openInWorkspace 同款）
    previewFile(f) {
      if (!f || !f.url) return
      const ext = (f.name || '').split('.').pop().toLowerCase()
      const isImage = this.isFileImage(f)
      const isVideo = this.isFileVideo(f)
      const isAudio = this.isFileAudio(f)
      const kind = isImage ? 'image' : isVideo ? 'video' : isAudio ? 'audio' : 'file'
      const previewItem = {
        id: 'chat-file-preview',
        previewMode: 'file',
        title: f.name || '文件预览',
        meta: this.formatSize(f.size) || '',
        url: f.url || '',
        kind,
        fileName: f.name || '',
        mimeType: f.mimeType || f.type || '',
        fileSizeStr: this.formatSize(f.size) || '',
        ext
      }
      this.$store.commit('SET_GROUP_WORKSPACE_PREVIEW', previewItem)
      this.$store.commit('SET_GROUP_WORKSPACE_TAB', 'preview')
      this.$store.commit('SET_WORKSPACE_OPEN', true)
      if (kind === 'image') this.$store.commit('SET_FULLSCREEN_PREVIEW', previewItem)
    },
    // ── 自定义 audio 播放器（与 TelegramChat 同源） ──
    audioId(item) { return 'pin_' + (item.id || item.messageId) },
    _getAudio(id) {
      const r = this.$refs['audio_' + id]
      if (!r) return null
      return Array.isArray(r) ? r[0] : r
    },
    _ensureAudioState(id) {
      if (!this.audioState[id]) {
        this.$set(this.audioState, id, { playing: false, current: 0, duration: 0 })
      }
    },
    toggleAudio(id) {
      this._ensureAudioState(id)
      const el = this._getAudio(id)
      if (!el) return
      Object.keys(this.audioState).forEach(k => {
        if (k !== String(id) && this.audioState[k] && this.audioState[k].playing) {
          const other = this._getAudio(k)
          if (other) other.pause()
          this.$set(this.audioState[k], 'playing', false)
        }
      })
      if (el.paused) {
        el.play().then(() => this.$set(this.audioState[id], 'playing', true))
          .catch(err => { console.warn('[audio] play failed:', err.message); this.$set(this.audioState[id], 'playing', false) })
      } else {
        el.pause()
        this.$set(this.audioState[id], 'playing', false)
      }
    },
    onAudioTimeUpdate(id, e) {
      this._ensureAudioState(id)
      this.$set(this.audioState[id], 'current', e.target.currentTime)
      this.$set(this.audioState[id], 'duration', e.target.duration || 0)
    },
    onAudioMeta(id, e) {
      this._ensureAudioState(id)
      this.$set(this.audioState[id], 'duration', e.target.duration || 0)
    },
    onAudioEnded(id) {
      this._ensureAudioState(id)
      this.$set(this.audioState[id], 'playing', false)
      this.$set(this.audioState[id], 'current', 0)
      const el = this._getAudio(id)
      if (el) el.currentTime = 0
    },
    seekAudio(id, e) {
      this._ensureAudioState(id)
      const el = this._getAudio(id)
      if (!el) return
      const rect = e.currentTarget.getBoundingClientRect()
      const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
      const dur = el.duration || this.audioState[id].duration || 0
      if (dur) el.currentTime = ratio * dur
    },
    audioProgress(id) {
      const s = this.audioState[id]
      if (!s || !s.duration) return 0
      return Math.min(100, (s.current / s.duration) * 100)
    },
    audioDisplayTime(id) {
      const s = this.audioState[id]
      const fmt = t => {
        if (!t || isNaN(t)) return '0:00'
        const m = Math.floor(t / 60)
        const sec = Math.floor(t % 60)
        return m + ':' + String(sec).padStart(2, '0')
      }
      if (!s) return '0:00'
      if (s.playing || s.current > 0) return fmt(s.current)
      return fmt(s.duration)
    }
  },
  beforeDestroy() {
    // 卸载时若菜单还开着，统一清掉所有 document/window 监听
    this.closeCtxMenu()
    // 把 teleport 到 body 的菜单 DOM 拿回来交给 Vue 销毁（避免泄漏）
    const el = this.$refs.ctxMenu
    if (el && el.parentNode === document.body) document.body.removeChild(el)
  }
}
</script>
